import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { message } from "antd";
import { IoCreate } from "react-icons/io5";
import DataParser from "../../utlits/Dataparser";
import UserNotesServices from "../../Services/UserNotesServices";

// Header "quick note" button (Notes module): same button and dropdown panel as the notification bell.
// Enter saves the note and readies the box for the next one; Shift+Enter adds a new line.
const QuickNote = () => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);

  const permissions = DataParser.CompanyUserPermissions() || [];
  const modules = DataParser.AllowedCompanyModules() || [];
  const visible = Number(DataParser.CompanyId()) > 0 && modules.includes("Notes") && permissions.includes("notes-create");
  const canViewAll = permissions.includes("notes-list");

  // focus the box whenever the panel opens
  useEffect(() => {
    const el = buttonRef.current;
    if (!el) return undefined;
    const onShown = () => setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    el.addEventListener("shown.bs.dropdown", onShown);
    return () => el.removeEventListener("shown.bs.dropdown", onShown);
  }, [visible]);

  if (!visible) return null;

  const hide = () => {
    const el = document.getElementById("page-header-quicknote-dropdown");
    if (el && window.bootstrap) {
      const dd = window.bootstrap.Dropdown.getInstance(el);
      if (dd) dd.hide();
    }
  };

  const save = () => {
    const content = text.trim();
    if (!content || saving) return;
    setSaving(true);
    UserNotesServices.createNote({ content })
      .then((res) => {
        if (res?.Good) {
          message.success("Note saved");
          setText("");
          window.dispatchEvent(new CustomEvent("planit:notesUpdated"));
          setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
        } else {
          message.warning(res?.data || "Could not save the note.");
        }
      })
      .catch((err) => message.error(err?.response?.data?.data || "Could not save the note."))
      .finally(() => setSaving(false));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      save();
    }
  };

  return (
    <div className="dropdown d-inline-block" style={{ position: "relative" }}>
      <button
        ref={buttonRef}
        type="button"
        className="btn header-item noti-icon waves-effect dropdown-toggle"
        id="page-header-quicknote-dropdown"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        aria-haspopup="true"
        aria-expanded="false"
        title="Quick note"
        // no right padding: keeps the gap to the bell the same as the bell's gap to the profile picture
        style={{ paddingRight: 0 }}
      >
        {/* Same 22px box as the bell, so both share one centre. The note glyph has more empty space than
            the bell's, so it is drawn 25px and centred on that box (1.5px over each side). */}
        <span style={{ position: "relative", display: "inline-block", width: "22px", height: "22px", lineHeight: "22px", verticalAlign: "middle" }}>
          {/* lifted 2px: the glyph's heavy part (the square) sits low in its box, so it looked lower than the bell */}
          <IoCreate style={{ fontSize: "25px", color: "#1D1D1D", display: "block", margin: "-1.5px", position: "relative", top: "-2px" }} />
        </span>
      </button>

      <div
        className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0 notification"
        aria-labelledby="page-header-quicknote-dropdown"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 pb-0">
          <div className="row align-items-center">
            <div className="col">
              <h5 className="m-0 font-size-16">Quick note</h5>
            </div>
            {canViewAll && (
              <div className="col-auto">
                <Link
                  to={`/${DataParser.CompanyIdentifierRole()}/notes`}
                  className="small"
                  style={{ color: "#0DDFAB", fontWeight: 600, textDecoration: "none" }}
                  onClick={hide}
                >
                  View all
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="p-3">
          <div className="float-input-wrap mb-2">
            <textarea
              id="quickNoteContent"
              ref={inputRef}
              placeholder=" "
              rows={3}
              maxLength={5000}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              style={{ paddingTop: "18px", resize: "vertical" }}
            />
            <label htmlFor="quickNoteContent">Type a note...</label>
          </div>
          <div className="d-flex align-items-center justify-content-between" style={{ gap: "8px" }}>
            <p className="font-size-12 text-muted mb-0">Enter to save · Shift+Enter for a new line</p>
            <button
              type="button"
              className="btn btn-primary waves-effect waves-light"
              style={{ borderRadius: "5px", fontSize: "13px" }}
              disabled={saving || !text.trim()}
              onClick={save}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickNote;
